import { Controller } from '@hotwired/stimulus';

import {
  updateDocumentUserState,
  patchDocumentUserState,
  applyCaseExperienceTurboStreamsFromPayload,
} from '../../utils/case_experience/case_experience_in_rails';
import { postCaseExperienceLogEvent } from '../../utils/case_experience/case_experience_api';

export default class extends Controller {
  static targets = ['content', 'indicator'];
  static values = {
    sectionLevel: Number,
    state: Object,
    aiEnabled: { type: Boolean, default: false },
    aiDisplayFlag: { type: String, default: null },
    vpeEnabled: { type: Boolean, default: false },
    aiFallbackCards: { type: Boolean, default: false },
    documentId: Number,
    userId: Number,
    nodeUid: String,
  };

  domSectionLevel() {
    const v = this.sectionLevelValue;
    if (typeof v === 'number' && !Number.isNaN(v)) return v;
    const raw = this.element.getAttribute('data-case-experience--section-collapse-section-level-value');
    if (raw === undefined || raw === '') return null;
    const n = parseInt(raw, 10);
    return Number.isNaN(n) ? null : n;
  }

  shouldTrackTimeWhileCollapsedOnConnect() {
    return this.domSectionLevel() === 1;
  }

  connect() {
    this.syncDetailsAccordionState();
    this.syncCoreConceptAccordionState();
    this.updateUI();

    const isLogProgress = this.data.get('log-progress') === 'true';
    if (isLogProgress) {
      void this.submitDocumentUserLog(this.documentIdValue, this.nodeUidValue, 'OpenedDocumentLog');
    }

    this.timeStarted = null;
    const expandedOrTwoColTracked =
      this.shouldTrackTimeWhileCollapsedOnConnect() || this.stateValue?.collapsed !== true;
    if (expandedOrTwoColTracked) {
      this.startTimeTracking();
    }
  }

  disconnect() {
    this.stopTimeTracking();
  }

  async toggle(event) {
    if (this.isDetailsAccordion() || this.isCoreConceptAccordion()) {
      return;
    }

    if (this.domSectionLevel() === 1) {
      if (event) {
        event.preventDefault();
        event.stopPropagation();
      }
      return;
    }

    if (event) {
      event.preventDefault();
      event.stopPropagation();
    }

    if (this.stateValue?.collapsed === false) {
      this.stopTimeTracking();
      this.stateValue = { ...this.stateValue, collapsed: true };
      this.updateUI();
      this.persistState();
      return;
    }

    if (this.stateValue?.collapsed === true) {
      await this.submitDocumentUserLog(this.documentIdValue, this.nodeUidValue, 'OpenedSectionLog');
    }

    this.stateValue = { ...this.stateValue, collapsed: false };

    this.startTimeTracking();

    this.updateUI();
    this.persistState();
  }

  syncDetails(event) {
    this.syncDetailsOpen(event);
  }

  syncDetailsOpen(event) {
    if (!this.isDetailsAccordion()) return;

    const details = event.currentTarget;
    const collapsed = !details.open;

    if (collapsed) {
      this.stopTimeTracking();
    } else {
      if (this.stateValue?.collapsed === true) {
        void this.submitDocumentUserLog(this.documentIdValue, this.nodeUidValue, 'OpenedSectionLog');
      }
      this.startTimeTracking();
    }

    this.stateValue = { ...this.stateValue, collapsed };
    this.updateSectionCollapsedClasses(collapsed);

    const summary = details.querySelector('summary');
    if (summary) {
      summary.setAttribute('aria-expanded', collapsed ? 'false' : 'true');
    }

    this.persistState();
  }

  syncCoreConceptDetails(event) {
    if (!this.isCoreConceptAccordion()) return;

    const details = event.currentTarget;
    const collapsed = !details.open;

    if (collapsed) {
      this.stopTimeTracking();
    } else {
      if (this.stateValue?.collapsed === true) {
        void this.submitDocumentUserLog(this.documentIdValue, this.nodeUidValue, 'OpenedSectionLog');
      }
      this.startTimeTracking();
      this.focusCoreConceptPanel();
    }

    this.stateValue = { ...this.stateValue, collapsed };
    this.updateSectionCollapsedClasses(collapsed);

    const summary = details.querySelector('.aq-core-concept-accordion__summary');
    if (summary) {
      summary.setAttribute('aria-expanded', collapsed ? 'false' : 'true');
    }

    this.persistState();
  }

  focusCoreConceptPanel() {
    const accordion = this.coreConceptAccordion();
    if (!accordion) return;

    const panel = accordion.querySelector('.aq-core-concept-accordion__body');
    if (!panel) return;

    panel.setAttribute('tabindex', '-1');
    panel.focus({ preventScroll: true });
  }

  handleKeydown(event) {
    if (this.isDetailsAccordion() || this.isCoreConceptAccordion()) {
      return;
    }

    if (this.domSectionLevel() === 1) {
      return;
    }

    if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault();
      this.toggle(event);
      const btn = event.target;
      if (btn && btn.classList && btn.classList.contains('aet-plus-minus-button')) {
        const buttons = Array.from(document.querySelectorAll('.aet-plus-minus-button')).filter(
          b => b.getAttribute('data-level') === '1' && b.offsetParent !== null
        );
        buttons.forEach(b => b.classList.remove('current-element'));
        btn.classList.add('current-element');
      }
      return;
    }

    if (event.key === 'ArrowRight' || event.key === 'ArrowLeft') {
      const buttons = Array.from(document.querySelectorAll('.aet-plus-minus-button')).filter(
        btn => btn.getAttribute('data-level') === '1' && btn.offsetParent !== null
      );
      const idx = buttons.indexOf(document.activeElement);
      if (idx === -1) return;
      event.preventDefault();
      let nextIdx;
      if (event.key === 'ArrowRight') {
        nextIdx = (idx + 1) % buttons.length;
      } else {
        nextIdx = (idx - 1 + buttons.length) % buttons.length;
      }
      setTimeout(() => {
        const nextButton = buttons[nextIdx];
        nextButton.focus();
        buttons.forEach((button, buttonIdx) => {
          if (buttonIdx === nextIdx) {
            button.classList.add('current-element');
          } else {
            button.classList.remove('current-element');
          }
        });
      }, 0);
    }
  }

  startTimeTracking() {
    if (this.timeStarted) return;

    this.timeStarted = Date.now();

    const nodeUid = this.element.dataset.uid;
    if (nodeUid) {
      this.element.classList.add('visited');

      this.dispatch('sectionVisited', {
        detail: { nodeUid: nodeUid, timestamp: this.timeStarted },
      });
    }
  }

  stopTimeTracking() {
    if (!this.timeStarted) return;

    const timeSpent = Math.floor((Date.now() - this.timeStarted) / 1000);
    const nodeUid = this.element.dataset.uid;

    if (nodeUid && timeSpent > 0) {
      this.dispatch('sectionTimeTracked', {
        detail: { nodeUid: nodeUid, timeSpent: timeSpent },
      });
    }

    this.timeStarted = null;
  }

  persistState() {
    if (typeof this.stateValue !== 'object') return;

    const payload = {
      documentId: this.documentIdValue,
      userId: this.userIdValue,
      nodeUid: this.nodeUidValue,
      key: 'collapsed',
      value: this.stateValue.collapsed,
    };

    if (this.isDetailsAccordion() || this.isCoreConceptAccordion()) {
      void patchDocumentUserState(payload).catch((error) => {
        console.error('Error updating document user state:', error);
      });
      return;
    }

    void this.submitDocumentUserState(
      payload.documentId,
      payload.userId,
      payload.nodeUid,
      payload.value,
    );
  }

  coreConceptCollapsed() {
    return this.stateValue?.collapsed !== false;
  }

  indicatorFallbackSelector() {
    return '[data-case-experience--section-collapse-target="indicator"]';
  }

  collapseIndicatorButton(indicator) {
    if (!indicator) return null;
    return indicator.matches('button') ? indicator : indicator.querySelector('button');
  }

  updateSummaryStatementAccordionButton(button, visualExpanded) {
    const accordionKind = button.dataset.summaryStatementAccordionKind;
    const labels =
      accordionKind === 'dialogue'
        ? { expanded: 'Collapse dialogue', collapsed: 'Expand dialogue' }
        : { expanded: 'Collapse guidelines', collapsed: 'Expand guidelines' };

    button.setAttribute('aria-expanded', visualExpanded ? 'true' : 'false');
    button.setAttribute('aria-label', visualExpanded ? labels.expanded : labels.collapsed);

    const chevronRoot = button.querySelector('.summary-statement-accordion-chevron');
    if (!chevronRoot) return;

    const collapsedChevron = chevronRoot.querySelector('.summary-statement-accordion-chevron--collapsed');
    const expandedChevron = chevronRoot.querySelector('.summary-statement-accordion-chevron--expanded');

    if (collapsedChevron) {
      collapsedChevron.classList.toggle('hidden', visualExpanded);
      collapsedChevron.classList.toggle('flex', !visualExpanded);
    }

    if (expandedChevron) {
      expandedChevron.classList.toggle('hidden', !visualExpanded);
      expandedChevron.classList.toggle('flex', visualExpanded);
    }
  }

  collapsedForChrome() {
    if (this.isDetailsAccordion()) {
      return this.stateValue?.collapsed !== false;
    }

    if (this.isCoreConceptAccordion()) {
      return this.coreConceptCollapsed();
    }

    if (this.domSectionLevel() === 1) {
      return false;
    }
    return Boolean(this.stateValue?.collapsed);
  }

  isDetailsAccordion() {
    return this.detailsAccordion() != null;
  }

  detailsAccordion() {
    return this.element.querySelector(
      ':scope > details[data-action*="case-experience--section-collapse#syncDetailsOpen"], ' +
        ':scope > details[data-action*="case-experience--section-collapse#syncDetails"]',
    );
  }

  syncDetailsAccordionState() {
    if (!this.isDetailsAccordion()) return;

    const accordion = this.detailsAccordion();
    if (!accordion) return;

    const summary = accordion.querySelector('summary');
    const expanded = this.stateValue?.collapsed === false;

    if (accordion.tagName === 'DETAILS') {
      accordion.open = expanded;
    }

    if (summary) {
      summary.setAttribute('aria-expanded', expanded ? 'true' : 'false');
    }
  }

  isCoreConceptAccordion() {
    return this.coreConceptAccordion() != null;
  }

  coreConceptAccordion() {
    return this.element.querySelector(':scope > .aq-core-concept-accordion');
  }

  syncCoreConceptAccordionState() {
    if (!this.isCoreConceptAccordion()) return;

    const accordion = this.coreConceptAccordion();
    if (!accordion) return;

    const summary = accordion.querySelector('.aq-core-concept-accordion__summary');
    const expanded = !this.coreConceptCollapsed();

    if (accordion.tagName === 'DETAILS') {
      accordion.open = expanded;
    }

    if (summary) {
      summary.setAttribute('aria-expanded', expanded ? 'true' : 'false');
    }
  }

  collapseToggleButton(indicator) {
    if (!indicator) return null;
    return indicator.matches('button, summary')
      ? indicator
      : indicator.querySelector('button, summary');
  }

  updateSectionCollapsedClasses(collapsed) {
    this.element.classList.toggle('collapsed', collapsed);
    this.element.classList.toggle('expanded', !collapsed);
  }

  updateUI() {
    const section = this.element;
    section.classList.remove('doc-section--hide-for-ai-pair');
    const indicatorFallback = this.indicatorFallbackSelector();
    const indicator = this.hasIndicatorTarget
      ? this.indicatorTarget
      : section.querySelector(indicatorFallback);
    const collapsed = this.collapsedForChrome();

    const hideForAiOffPair =
      this.vpeEnabledValue &&
      this.aiEnabledValue &&
      this.aiDisplayFlagValue === 'aiOff' &&
      !this.aiFallbackCardsValue;

    const visualExpanded = !collapsed;

    this.updateSectionCollapsedClasses(collapsed);

    if (this.isDetailsAccordion()) {
      this.syncDetailsAccordionState();
    } else if (this.isCoreConceptAccordion()) {
      this.syncCoreConceptAccordionState();
    } else {
      const content = this.hasContentTarget
        ? this.contentTarget
        : section.querySelector('.doc-section-body');
      if (content) {
        content.toggleAttribute('hidden', collapsed);
      }
    }

    if (indicator && !this.isDetailsAccordion() && !this.isCoreConceptAccordion()) {
      const button = this.collapseIndicatorButton(indicator) || this.collapseToggleButton(indicator);
      if (button && (button.matches('button') || button.matches('summary'))) {
        if (button.dataset.summaryStatementAccordionKind) {
          this.updateSummaryStatementAccordionButton(button, visualExpanded);
        } else if (button.matches('button')) {
          button.className = visualExpanded
            ? 'icon-close-box aet-plus-minus-button'
            : 'icon-open-box aet-plus-minus-button';
          button.setAttribute('aria-label', visualExpanded ? 'Collapse section' : 'Expand section');
          button.setAttribute('aria-expanded', visualExpanded ? 'true' : 'false');
        }
      }
    }

    const topperIndicator = section.querySelector(
      ':scope > .doc-section-header .teaching-point-topper-indicator',
    );
    if (topperIndicator) {
      const expandImg = topperIndicator.querySelector('.teaching-point-topper-circle-expand');
      const collapseImg = topperIndicator.querySelector('.teaching-point-topper-circle-collapse');
      if (expandImg && collapseImg) {
        expandImg.style.display = visualExpanded ? 'none' : 'block';
        collapseImg.style.display = visualExpanded ? 'block' : 'none';
      }
    }

    if (hideForAiOffPair) {
      section.style.display = 'none';
      section.classList.add('doc-section--hide-for-ai-pair');
    } else if (this.stateValue?.invisible === true) {
      section.style.display = 'none';
    } else {
      section.style.display = '';
    }
  }

  async submitDocumentUserState(documentId, userId, nodeUid, value) {
    if (!documentId || !userId || !nodeUid) return;

    try {
      await updateDocumentUserState({
        documentId: documentId,
        userId: userId,
        nodeUid: nodeUid,
        key: 'collapsed',
        value: value,
      });
    } catch (error) {
      console.error('Error updating document user state:', error);
    }
  }

  async submitDocumentUserLog(documentId, nodeUid, type) {
    if (!documentId || !nodeUid || !type) return;
    try {
      const json = await postCaseExperienceLogEvent(documentId, type, { node_uid: nodeUid });
      applyCaseExperienceTurboStreamsFromPayload(json);
    } catch (error) {
      console.error('Error posting case experience log event:', error);
    }
  }
}
